/**
 * abilityFamily.cues — synth recipes for the ability sound families
 * (plan 29 F3, lane BU 2026-10-11).
 *
 * One recipe per id in src/shared/content/abilitySoundRules.ts: eight families
 * (school timbres) × eight kinds (cast, launch, dash, three impact tiers, heal,
 * status), plus absorb and immune. A recipe is a kind SHAPE (the envelope and
 * layering that says "a bolt left", "a heavy blow landed", "a heal arrived")
 * voiced through a family TIMBRE (the oscillator, intervals and noise colour
 * that say fire / frost / shadow …), so every family reads as itself in every
 * kind and every kind reads the same across families.
 *
 * Anti machine-gun: each cue is a variation set of three takes (slight detune
 * and grain timing), plus per-play pitch jitter and the engine's dedup /
 * maxStack. Loudness sits in the existing mix: a medium impact's grains sum
 * near `combat.melee.hit`'s (1.5), a heavy one stays under twice that, and a
 * cast stays near the old blip (abilityFamily.cues.test.ts holds the bounds).
 * The synth engine renders all of it (SPEC.md §9.1); no sample assets.
 */

import type { Cue, NoiseDesc, SequenceDesc, SequenceStep, ToneDesc } from '../engine/audioTypes';
import {
  ABILITY_SOUND_FAMILIES, DEFENSE_CUES, abilityCueId,
  type AbilitySoundFamily, type AbilitySoundKind,
} from '../../../../shared/content/abilitySoundRules';

/** A family's voice: what every kind shape is played with. */
interface Timbre {
  /** Fundamental (Hz) the shapes are pitched from. */
  base: number;
  /** Body oscillator. */
  osc: OscillatorType;
  /** Interval of the second voice (frequency ratio). */
  interval: number;
  /** Noise colour for whooshes, crackle and impact texture. */
  noise: { type: BiquadFilterType; freq: number; gain: number };
  /** Pitch motion of the cast swell: >1 rises, <1 falls. */
  rise: number;
  /** Extra short noise grains (crackle) on impacts and casts; 0 = none. */
  crackle: number;
}

export const FAMILY_TIMBRES: Record<AbilitySoundFamily, Timbre> = {
  // Air and weight: a crisp whoosh over a punchy body with impact snap.
  physical: { base: 160, osc: 'triangle', interval: 0.5, noise: { type: 'lowpass', freq: 1800, gain: 0.85 }, rise: 0.8, crackle: 1 },
  // A bright major chime: sine fifths with an airy top.
  holy: { base: 523, osc: 'sine', interval: 1.5, noise: { type: 'highpass', freq: 6000, gain: 0.25 }, rise: 1.5, crackle: 0 },
  // A roaring saw with crackling band noise, rising.
  fire: { base: 180, osc: 'sawtooth', interval: 1.5, noise: { type: 'bandpass', freq: 1800, gain: 0.7 }, rise: 1.8, crackle: 2 },
  // Glassy high triangle octaves, hissing, falling.
  frost: { base: 880, osc: 'triangle', interval: 2, noise: { type: 'highpass', freq: 4500, gain: 0.45 }, rise: 0.7, crackle: 0 },
  // A shimmering fourth on triangle with a mid sparkle.
  arcane: { base: 440, osc: 'triangle', interval: 1.335, noise: { type: 'bandpass', freq: 3200, gain: 0.35 }, rise: 2, crackle: 1 },
  // A woody, rustling third.
  nature: { base: 262, osc: 'triangle', interval: 1.25, noise: { type: 'bandpass', freq: 700, gain: 0.55 }, rise: 1.25, crackle: 0 },
  // A dark, dissonant saw (minor second) sinking into murk.
  shadow: { base: 110, osc: 'sawtooth', interval: 1.059, noise: { type: 'lowpass', freq: 500, gain: 0.6 }, rise: 0.6, crackle: 0 },
  // A buzzing square tritone with snapping high crackle.
  lightning: { base: 660, osc: 'square', interval: 1.414, noise: { type: 'highpass', freq: 2500, gain: 0.6 }, rise: 1.6, crackle: 3 },
};

/** One take of a recipe: detune ratio and grain-timing offset (ms). */
interface Take { detune: number; slip: number }
const TAKES: readonly Take[] = [
  { detune: 1, slip: 0 },
  { detune: 1.035, slip: 7 },
  { detune: 0.97, slip: 13 },
];

const tone = (freq: number, osc: OscillatorType, durationMs: number, gain: number, sweepTo?: number): ToneDesc => ({
  kind: 'tone', freq: Math.round(freq * 10) / 10, osc, durationMs, gain,
  ...(sweepTo !== undefined ? { sweepTo: Math.round(sweepTo * 10) / 10 } : {}),
});
const noise = (t: Timbre, durationMs: number, gainScale: number, freqScale = 1): NoiseDesc => ({
  kind: 'noise', durationMs,
  gain: Math.round(t.noise.gain * gainScale * 100) / 100,
  filter: { type: t.noise.type, freq: Math.round(t.noise.freq * freqScale) },
});
const at = (delayMs: number, desc: ToneDesc | NoiseDesc): SequenceStep => ({ delayMs, desc });

/** Short noise snaps after `start`, spaced by `gap` ms (fire / arcane / lightning crackle). */
function crackle(t: Timbre, start: number, gap: number, gain: number): SequenceStep[] {
  const out: SequenceStep[] = [];
  for (let i = 0; i < t.crackle; i++) out.push(at(start + i * gap, noise(t, 28, gain, 1.4)));
  return out;
}

/** Kind shapes: the layering and envelope of each moment, voiced by a timbre. */
const SHAPES: Record<AbilitySoundKind, (t: Timbre, k: Take) => SequenceStep[]> = {
  // Wind-up: a swell toward the family's pitch motion, a second voice entering, texture underneath.
  cast: (t, k) => [
    at(0, tone(t.base * k.detune, t.osc, 200, 0.42, t.base * k.detune * t.rise)),
    at(60 + k.slip, tone(t.base * t.interval * k.detune, t.osc, 160, 0.26, t.base * t.interval * k.detune * t.rise)),
    at(0, noise(t, 150, 0.4)),
    ...crackle(t, 40 + k.slip, 45, 0.3),
  ],
  // Launch: a fast upward zip with a whoosh — something leaves the hand.
  launch: (t, k) => [
    at(0, tone(t.base * 1.5 * k.detune, t.osc, 130, 0.38, t.base * 3.2 * k.detune)),
    at(0, noise(t, 110, 0.55, 1.2)),
    at(30 + k.slip, tone(t.base * t.interval * 2 * k.detune, 'sine', 90, 0.18)),
  ],
  // Dash / blink: a falling rush of noise and pitch — the body goes.
  dash: (t, k) => [
    at(0, noise(t, 190, 0.7, 1.6)),
    at(10 + k.slip, tone(t.base * 2.2 * k.detune, t.osc, 160, 0.24, t.base * 0.6 * k.detune)),
  ],
  // Impacts: noise burst + falling body; heavier = longer, lower, louder, with a sub.
  'impact.light': (t, k) => [
    at(0, noise(t, 70, 0.75, 0.9)),
    at(0, tone(t.base * 0.8 * k.detune, t.osc, 80, 0.55)),
    ...crackle(t, 12 + k.slip, 30, 0.35).slice(0, 1),
  ],
  'impact.medium': (t, k) => [
    at(0, noise(t, 120, 0.95, 0.75)),
    at(0, tone(t.base * 0.6 * k.detune, t.osc, 130, 0.78)),
    at(18 + k.slip, tone(t.base * 0.6 * t.interval * k.detune, 'sine', 90, 0.32)),
    ...crackle(t, 20 + k.slip, 32, 0.4).slice(0, 2),
  ],
  'impact.heavy': (t, k) => [
    at(0, noise(t, 230, 0.9, 0.55)),
    at(0, tone(t.base * 0.42 * k.detune, t.osc, 260, 0.8)),
    at(0, tone(55 * k.detune, 'sine', 300, 0.6, 32)),
    at(28 + k.slip, tone(t.base * 0.42 * t.interval * k.detune, 'sine', 180, 0.3)),
    ...crackle(t, 30 + k.slip, 40, 0.35),
  ],
  // Heal land: a rising three-note figure that holds its pitch, with an airy shimmer.
  heal: (t, k) => {
    const f = Math.min(t.base * 2, 1400) * k.detune;
    return [
      at(0, tone(f, 'sine', 220, 0.36, f)),
      at(70 + k.slip, tone(f * 1.25, 'sine', 220, 0.32, f * 1.25)),
      at(140 + k.slip, tone(f * t.interval * (t.interval < 1.2 ? 1.4 : 1), 'sine', 280, 0.3, f * t.interval * (t.interval < 1.2 ? 1.4 : 1))),
      at(40, noise(t, 260, 0.3, 1.5)),
    ];
  },
  // Status apply: a two-step lift on the family's body — something settles onto you.
  buff: (t, k) => [
    at(0, tone(t.base * 1.5 * k.detune, t.osc, 170, 0.3, t.base * 1.5 * k.detune * 1.06)),
    at(90 + k.slip, tone(t.base * 1.5 * t.interval * k.detune, t.osc, 230, 0.28, t.base * 1.5 * t.interval * k.detune * 1.06)),
    at(0, noise(t, 120, 0.3, 1.3)),
  ],
};

/** Mix + voice policy per kind: priority, dedup window, plays-at-once cap. */
const KIND_POLICY: Record<AbilitySoundKind, { priority: number; minIntervalMs: number; maxStack: number }> = {
  cast: { priority: 0.45, minIntervalMs: 40, maxStack: 2 },
  launch: { priority: 0.45, minIntervalMs: 40, maxStack: 3 },
  dash: { priority: 0.5, minIntervalMs: 80, maxStack: 2 },
  'impact.light': { priority: 0.42, minIntervalMs: 35, maxStack: 4 },
  'impact.medium': { priority: 0.52, minIntervalMs: 45, maxStack: 3 },
  'impact.heavy': { priority: 0.62, minIntervalMs: 60, maxStack: 2 },
  heal: { priority: 0.55, minIntervalMs: 60, maxStack: 2 },
  buff: { priority: 0.5, minIntervalMs: 80, maxStack: 2 },
};

/** Per-play pitch jitter (± semitones) on top of the three takes. */
export const ABILITY_CUE_PITCH_JITTER = 0.35;

/** Longest grain end of a sequence (ms) — the voice gate holds a slot this long. */
export function sequenceDurationMs(sound: SequenceDesc): number {
  return Math.max(...sound.steps.map((s) => s.delayMs + s.desc.durationMs));
}

function familyCue(family: AbilitySoundFamily, kind: AbilitySoundKind): Cue {
  const timbre = FAMILY_TIMBRES[family];
  const policy = KIND_POLICY[kind];
  return {
    id: abilityCueId(family, kind),
    bus: 'sfx',
    priority: policy.priority,
    spatial: true,
    minIntervalMs: policy.minIntervalMs,
    maxStack: policy.maxStack,
    pitchJitter: ABILITY_CUE_PITCH_JITTER,
    sound: TAKES.map((take): SequenceDesc => ({ kind: 'sequence', steps: SHAPES[kind](timbre, take) })),
  };
}

const DEFENSE: Cue[] = [
  {
    // A dull knock on a barrier: the hit was soaked.
    id: DEFENSE_CUES.absorb,
    bus: 'sfx', priority: 0.6, spatial: true, minIntervalMs: 60, maxStack: 2, pitchJitter: 0.3,
    sound: TAKES.map((k): SequenceDesc => ({ kind: 'sequence', steps: [
      at(0, tone(330 * k.detune, 'triangle', 140, 0.6, 290 * k.detune)),
      at(0, { kind: 'noise', durationMs: 90, gain: 0.55, filter: { type: 'bandpass', freq: 1200 } }),
      at(20 + k.slip, tone(165 * k.detune, 'sine', 160, 0.45, 150 * k.detune)),
    ] })),
  },
  {
    // A clean bell ting: nothing got through.
    id: DEFENSE_CUES.immune,
    bus: 'sfx', priority: 0.65, spatial: true, minIntervalMs: 60, maxStack: 2, pitchJitter: 0.2,
    sound: TAKES.map((k): SequenceDesc => ({ kind: 'sequence', steps: [
      at(0, tone(1320 * k.detune, 'sine', 300, 0.5, 1320 * k.detune)),
      at(25 + k.slip, tone(1980 * k.detune, 'sine', 260, 0.32, 1980 * k.detune)),
      at(0, { kind: 'noise', durationMs: 40, gain: 0.3, filter: { type: 'highpass', freq: 5000 } }),
    ] })),
  },
];

/** Every ability family cue (the ids of ABILITY_SOUND_CUE_IDS, in that order). */
export const ABILITY_FAMILY_CUES: Cue[] = [
  ...ABILITY_SOUND_FAMILIES.flatMap((family) =>
    (Object.keys(SHAPES) as AbilitySoundKind[]).map((kind) => familyCue(family, kind))),
  ...DEFENSE,
];
